import React, {
  ReactElement,
  useState,
  FormEvent,
  FormEventHandler,
  useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { History } from 'history';
import { Row, Col, Input, Checkbox, Button, message as Message } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { FaUserAlt, FaEnvelope, FaLock } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

import {
  AuthenticationRouteProps,
  AuthenticationParams,
} from './Authentication';

import { signUp } from '../../store/auth/actions';
import { AccountType } from '../../store/auth/types';
import { getAppGlobalState } from '../../store/app-globals/selectors';
import { setError, setLoading } from '../../store/app-globals/actions';

import UserIcon from '../../assets/images/png/registration-user.png';

const COOK_HEADER_TITLE = 'CREATE_COOK_ACCOUNT';
const GUEST_HEADER_TITLE = 'CREATE_GUEST_ACCOUNT';

export interface UserFormState {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  status: string;
}

export const SignUp: React.FC<AuthenticationRouteProps> = ({
  setHeaderTitle,
}): ReactElement => {
  const { t } = useTranslation();
  const { accountType } = useParams<AuthenticationParams>();
  const { isLoading, message, hasError, errorMessage } = useSelector(
    getAppGlobalState
  );
  const _accountType: AccountType =
    accountType === 'i offer' || accountType === 'i seek'
      ? accountType
      : 'i seek';

  const dispatch = useDispatch();
  const [user, setUser] = useState<UserFormState>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    status: 'active',
  });
  const [disabled, setDisabled] = useState(true);
  const [agreed, setAgreed] = useState(false);
  const [cookiesAgreed, setCookiesAgreed] = useState(false);
  const history: History = useHistory();

  const handleFieldChange = (field: keyof UserFormState): FormEventHandler => (
    e: FormEvent<HTMLInputElement>
  ): void => {
    setUser({ ...user, [field]: e.currentTarget.value });
  };

  const handleAgreedChange = (e: CheckboxChangeEvent): void =>
    setAgreed(e.target.checked);

  const handleCookiesAgreedChange = (e: CheckboxChangeEvent): void =>
    setCookiesAgreed(e.target.checked);

  const handleSubmit = (e: React.MouseEvent<HTMLElement, MouseEvent>): void => {
    // console.log(e);
    if (user.password !== user.confirmPassword) {
      Message.error('Password did not match!');
      return;
    }
    if (user.firstName === '' || user.lastName === '') {
      Message.error('Firstname and lastname is required');
      return;
    }
    dispatch(signUp(_accountType, { ...user }));
    // dispatch(syncUser());
  };

  const onChangeAccountType: React.EffectCallback = () => {
    if (setHeaderTitle)
      setHeaderTitle(
        accountType === 'i offer' ? t(COOK_HEADER_TITLE) : t(GUEST_HEADER_TITLE)
      );
  };

  const validate: React.EffectCallback = () => {
    setDisabled(
      (Object.keys(user) as Array<keyof UserFormState>).every(
        key => user[key] === ''
      ) ||
        // user.password !== user.confirmPassword ||
        !agreed ||
        !cookiesAgreed
    );
  };

  const error = () => {
    if (hasError) {
      Message.error(errorMessage);
      dispatch(setError(false, ''));
      return;
    }

    if (!isLoading && message) {
      Message.success(message);
      dispatch(setLoading(false, ''));
      history.push(`/authentication/login/email/${accountType}`);
    }
  };

  useEffect(onChangeAccountType, [accountType]);
  useEffect(validate, [user, agreed, cookiesAgreed]);
  useEffect(error, [hasError, message]);
  return (
    <Row className="sign-up" justify="space-between">
      <Col span={24} flex="0 0 200px" className="user-icon-container">
        <img src={UserIcon} alt="registration-user" />
      </Col>
      <Row>
        <Col span={24} className="input-field-container">
          <Input
            size="large"
            placeholder={t('NAME')}
            className="input-field"
            prefix={<FaUserAlt color="rgba(255,255,255,0.7)" />}
            value={user.firstName}
            onChange={handleFieldChange('firstName')}
          />
        </Col>
        <Col span={24} className="input-field-container">
          <Input
            size="large"
            placeholder={t('SURNAME')}
            className="input-field"
            prefix={<FaUserAlt color="rgba(255,255,255,0.7)" />}
            value={user.lastName}
            onChange={handleFieldChange('lastName')}
          />
        </Col>
        <Col span={24} className="input-field-container">
          <Input
            size="large"
            placeholder="E-mail"
            className="input-field"
            prefix={<FaEnvelope color="rgba(255,255,255,0.7)" />}
            value={user.email}
            onChange={handleFieldChange('email')}
          />
        </Col>
        <Col span={24} className="input-field-container">
          <Input.Password
            size="large"
            placeholder={t('PASSWORD')}
            className="input-field"
            prefix={<FaLock color="rgba(255,255,255,0.7)" />}
            value={user.password}
            onChange={handleFieldChange('password')}
          />
        </Col>
        <Col span={24} className="input-field-container">
          <Input.Password
            size="large"
            placeholder={t('PASSWORD_AGAIN')}
            className="input-field"
            prefix={<FaLock color="rgba(255,255,255,0.7)" />}
            value={user.confirmPassword}
            onChange={handleFieldChange('confirmPassword')}
          />
        </Col>
        <Col span={24} className="input-field-container with-margins">
          <Checkbox checked={agreed} onChange={handleAgreedChange}>
            {t('I_AGREE_WITH')}
            <button onClick={() => history.push('/settings/terms')}>
              {t('TERMS_AND_CONDITIONS')}
            </button>
          </Checkbox>
        </Col>
        <Col span={24} className="input-field-container with-margins">
          <Col className="input-checkbox">
            <Checkbox
              checked={cookiesAgreed}
              onChange={handleCookiesAgreedChange}
            >
              {t('COOKIES')}{' '}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://policies.google.com/technologies/cookies"
              >
                {t('READ_MORE')}
              </a>
            </Checkbox>
          </Col>
        </Col>
      </Row>
      <Col span={24} className="login-button-container padded-bottom">
        <Button
          shape="round"
          className="login-button login-button-secondary"
          onClick={handleSubmit}
          disabled={disabled || isLoading}
          loading={isLoading}
        >
          {isLoading ? t(`${message}`) : t('CREATE')}
        </Button>
      </Col>
    </Row>
  );
};
